import React, { createContext, useContext, useMemo, useState, useEffect, ReactNode } from 'react';

import { ru as extraRu } from '../i18n/ru';
import { en as extraEn } from '../i18n/en';

type Language = 'ru' | 'en';

type Translations = Record<string, Record<string, string>>;

interface LanguageContextValue {
  language: Language;
  t: (key: string, params?: Record<string, string | number>) => string;
  toggleLanguage: () => void;
  setLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

const translations: Translations = {
  ru: {
    'nav.home': 'Главная',
    'nav.matchmaking': 'Поиск матча',
    'nav.profile': 'Профиль',
    'nav.stats': 'Статистика',
    'nav.inventory': 'Инвентарь',
    'nav.clans': 'Кланы',
    'nav.subscription': 'Премиум',
    'nav.support': 'Поддержка',
    'nav.admin': 'Админ панель',

    'social.telegram': 'Telegram — CS Legasy',
    'social.youtube': 'YouTube — CS Legasy',
    'social.followUs': 'Мы в соцсетях',

    'header.notifications': 'Уведомления',
    'header.markAllRead': 'Прочитать все',
    'header.noNotifications': 'Нет уведомлений',
    'header.userDefault': 'Пользователь',
    'header.level': 'Уровень {value}',
    'header.profile': 'Профиль',
    'header.subscription': 'Премиум',
    'header.logout': 'Выйти',
    'header.logout_success': 'Вы успешно вышли из аккаунта',

    'test.friend_request.title': 'Новый запрос в друзья',
    'test.friend_request.message': 'Player123 хочет добавить вас в друзья',
    'test.match_found.title': 'Матч найден! ',
    'test.match_found.message': 'Ваш матч готов к началу. Перейдите в лобби.',
    'test.clan_invite.title': 'Приглашение в клан',
    'test.clan_invite.message': 'Клан "Elite Warriors" приглашает вас присоединиться',

    'time.just_now': 'Только что',
    'time.minutes_ago': '{value}м назад',
    'time.hours_ago': '{value}ч назад',
    'time.locale': 'ru-RU',

    // Common
    'common.play': 'Играть',
    'common.cancel': 'Отмена',
    'common.save': 'Сохранить',
    'common.update': 'Обновить',
    'common.export': 'Экспорт данных',
    'common.search': 'Поиск',
    'common.filters': 'Фильтры',
    'common.filters_search': 'Фильтры и поиск',
    'common.back': 'Назад',
    'common.next': 'Вперед',
    'common.page_of': 'Страница {current} из {total}',
    'common.shown_of': 'Показано {shown} из {total}',
    'common.soon': 'Скоро',

    // Lobby
    'lobby.title': 'Добро пожаловать в ROMAN FACEIT',
    'lobby.subtitle': 'Готовы к новым вызовам? Найдите матч или изучите свои достижения',
    'lobby.stat.matches': 'Матчей сыграно',
    'lobby.stat.winrate': 'Процент побед',
    'lobby.stat.rating': 'Очков рейтинга',
    'lobby.stat.level': 'Уровень',
    'lobby.feature.find_match.title': 'Найти матч',
    'lobby.feature.find_match.desc': 'Присоединяйтесь к поиску матча и сражайтесь с другими игроками',
    'lobby.feature.inventory.title': 'Инвентарь',
    'lobby.feature.inventory.desc': 'Управляйте своими предметами и скинами',
    'lobby.feature.clans.title': 'Кланы',
    'lobby.feature.clans.desc': 'Присоединяйтесь к клану или создайте свой',
    'lobby.feature.stats.title': 'Статистика',
    'lobby.feature.stats.desc': 'Просмотрите свою статистику и отслеживайте прогресс',
    'lobby.friends_online': 'Друзья онлайн',
    'lobby.status.online': 'В сети',
    'lobby.status.ingame': 'В игре',
    'lobby.invite': 'Пригласить',
    'lobby.recent_matches': 'Последние матчи',

    // Matchmaking
    'mm.title': 'Поиск матча',
    'mm.subtitle': 'Настройте параметры и найдите подходящий матч',
    'mm.type': 'Тип игры',
    'mm.type.solo': 'Одиночная игра',
    'mm.type.team': 'Командная игра',
    'mm.map': 'Карта',
    'mm.map.any': 'Любая карта',
    'mm.server': 'Сервер',
    'mm.server.any': 'Любой сервер',
    'mm.mode': 'Режим игры',
    'mm.start_search': 'Начать поиск',
    'mm.stop_search': 'Остановить поиск',
    'mm.toast.started': 'Поиск матча начат!\u00A0',
    'mm.toast.stopped': 'Поиск матча остановлен',
    'mm.status.searching': 'Поиск матча...',
    'mm.status.description': 'Ищем подходящих игроков для вашего матча',
    'mm.status.cancel': 'Отменить поиск',
    'mm.queue.info': 'Информация об очереди',
    'mm.queue.players': 'Игроков в очереди',
    'mm.queue.eta': 'Ожидаемое время',
    'mm.queue.rank': 'Ваш ранг',
    'mm.found.title': 'Матч найден!',
    'mm.found.map': 'Карта:',
    'mm.found.mode': 'Режим:',
    'mm.found.server': 'Сервер:',
    'mm.found.avg_rank': 'Средний ранг:',
    'mm.accept': 'Принять',
    'mm.decline': 'Отклонить',
    'mm.toast.accepted': 'Матч принят! Подключение к серверу...',
    'mm.toast.declined': 'Матч отклонен',

    // Profile
    'profile.edit_profile': 'Редактировать профиль',
    'profile.tabs.overview': 'Обзор',
    'profile.tabs.edit': 'Редактировать',
    'profile.tabs.achievements': 'Достижения',
    'profile.updated': 'Профиль обновлен!',
    'profile.logout_success': 'Вы успешно вышли из аккаунта',
    'profile.section.main': 'Основная информация',
    'profile.username': 'Имя пользователя',
    'profile.username_placeholder': 'Введите имя пользователя',
    'profile.email': 'Email',
    'profile.email_placeholder': 'Введите email',
    'profile.avatar': 'Аватар',
    'profile.background': 'Фон профиля',
    'profile.background_drop': 'Перетащите изображение для фона или нажмите для выбора',
    'profile.drop.active': 'Отпустите файл здесь',
    'profile.drop.idle': 'Перетащите изображение или нажмите для выбора',
    'profile.btn.cancel': 'Отмена',
    'profile.btn.logout': 'Выйти из аккаунта',
    'profile.btn.save': 'Сохранить',
    'profile.avatar_updated': 'Аватар обновлен!',
    'profile.level_elo': 'Уровень {level} • ELO {elo}',
    'profile.stats.matches': 'Матчей',
    'profile.stats.wins': 'Побед',
    'profile.stats.losses': 'Поражений',
    'profile.stats.winrate': 'Винрейт',
    'profile.metrics.winrate': 'Винрейт',
    'profile.metrics.winrate_desc': 'Процент выигранных матчей',
    'profile.metrics.kd': 'K/D Ratio',
    'profile.metrics.kd_desc': 'Соотношение убийств к смертям',
    'profile.metrics.total_matches': 'Всего матчей',
    'profile.metrics.total_matches_desc': 'Общее количество сыгранных матчей',
    'profile.metrics.best_map': 'Любимая карта',
    'profile.metrics.best_map_desc': 'Карта с наибольшим количеством матчей',
    'profile.metrics.avg_score': 'Средний счет',
    'profile.metrics.avg_score_desc': 'Средний счет за матч',
    'profile.metrics.rank': 'Текущий ранг',
    'profile.metrics.rank_desc': 'Ваш текущий ранг в системе',
    'profile.achievements.soon': 'Система достижений будет добавлена в следующих обновлениях',

    // Inventory
    'inventory.title': 'Инвентарь',
    'inventory.add_item': 'Добавить предмет',
    'inventory.stats': 'Статистика',
    'inventory.total_items': 'Всего предметов',
    'inventory.stattrak_items': 'StatTrak предметов',
    'inventory.legendary_items': 'Легендарных предметов',
    'inventory.custom_items': 'Пользовательских предметов',
    'inventory.filters_search': 'Фильтры и поиск',
    'inventory.search': 'Поиск',
    'inventory.search_placeholder': 'Поиск по названию...',
    'inventory.type': 'Тип предмета',
    'inventory.type.all': 'Все типы',
    'inventory.type.weapon': 'Оружие',
    'inventory.type.knife': 'Ножи',
    'inventory.type.glove': 'Перчатки',
    'inventory.type.sticker': 'Наклейки',
    'inventory.type.agent': 'Агенты',
    'inventory.rarity': 'Редкость',
    'inventory.rarity.all': 'Все редкости',
    'inventory.rarity.common': 'Обычные',
    'inventory.rarity.uncommon': 'Необычные',
    'inventory.rarity.rare': 'Редкие',
    'inventory.rarity.epic': 'Эпические',
    'inventory.rarity.legendary': 'Легендарные',
    'inventory.condition': 'Состояние',
    'inventory.condition.all': 'Все состояния',
    'inventory.condition.factory_new': 'Новое с завода',
    'inventory.condition.minimal_wear': 'Минимальный износ',
    'inventory.condition.field_tested': 'После полевых испытаний',
    'inventory.condition.well_worn': 'Сильно поношенное',
    'inventory.condition.battle_scarred': 'Закаленное в боях',
    'inventory.empty.title': 'Инвентарь пуст',
    'inventory.empty.desc': 'Здесь будут отображаться ваши предметы',
    'inventory.inspect': 'Осмотр',
    'inventory.toast.inspect': 'Осмотр предмета: {name}',
    'inventory.toast.added': 'Предмет "{name}" добавлен в инвентарь! ',
    'inventory.delete': 'Удалить',
    'inventory.sort': 'Сортировка',
    'inventory.sort.newest': 'Сначала новые',
    'inventory.sort.oldest': 'Сначала старые',
    'inventory.sort.name_asc': 'По имени (А–Я)',
    'inventory.sort.name_desc': 'По имени (Я–А)',
    'inventory.sort.rarity_high': 'Редкость: высокая',
    'inventory.sort.rarity_low': 'Редкость: низкая',
    'inventory.sort.float_low': 'Float: низкий',
    'inventory.sort.float_high': 'Float: высокий',
    'inventory.loading': 'Загрузка инвентаря…',
    'inventory.page': 'Страница {page} из {total}',
    'inventory.items_count': '{count} предметов',
    'inventory.add_first': 'Добавить первый предмет',

    // Stats
    'stats.title': 'Статистика',
    'stats.export': 'Экспорт данных',
    'stats.refresh': 'Обновить',
    'stats.total_matches': 'Всего матчей',
    'stats.total_matches_delta': '+12% за месяц',
    'stats.next_level_in': 'До следующего уровня: {value} матчей',
    'stats.winrate': 'Винрейт',
    'stats.winrate_goal': 'Цель: 70%',
    'stats.kd_ratio': 'K/D Ratio',
    'stats.kd_delta': '-0.05 за месяц',
    'stats.kd_avg': 'Средний за матч: 1.2',
    'stats.elo': 'ELO рейтинг',
    'stats.elo_delta': '+150 за месяц',
    'stats.elo_next': 'До следующего ранга: 250 ELO',
    'stats.avg_time': 'Среднее время игры',
    'stats.avg_time_delta': '+0.3ч за неделю',
    'stats.total_time': 'Всего игрового времени: 120ч',
    'stats.avg_damage': 'Средний урон',
    'stats.avg_damage_best': 'Лучший результат: 156.7',
    'stats.filters': 'Фильтры',
    'stats.filter.map': 'Карта',
    'stats.filter.all_maps': 'Все карты',
    'stats.filter.period': 'Период',
    'stats.filter.last7': 'Последние 7 дней',
    'stats.filter.last30': 'Последние 30 дней',
    'stats.filter.last90': 'Последние 90 дней',
    'stats.filter.all_time': 'Все время',
    'stats.filter.result': 'Результат',
    'stats.filter.all_matches': 'Все матчи',
    'stats.filter.only_wins': 'Только победы',
    'stats.filter.only_losses': 'Только поражения',
    'stats.performance_metrics': 'Метрики производительности',
    'stats.player_skills': 'Навыки игрока',
    'stats.wins_losses': 'Соотношение побед/поражений',
    'stats.elo_trend': 'Динамика ELO',
    'stats.by_maps': 'Статистика по картам',
    'stats.activity_by_days': 'Активность по дням',
    'stats.chart_loading': 'Загрузка…',
    'stats.chart_empty': 'Нет данных для выбранных фильтров',
    'stats.table.title': 'История матчей',
    'stats.table.search_placeholder': 'Поиск по карте или режиму...',
    'stats.table.date': 'Дата',
    'stats.table.map': 'Карта',
    'stats.table.mode': 'Режим',
    'stats.table.server': 'Сервер',
    'stats.table.result': 'Результат',
    'stats.table.score': 'Счет',
    'stats.table.duration': 'Длительность',
    'stats.table.actions': 'Действия',
    'stats.result.win': 'Победа',
    'stats.result.loss': 'Поражение',
    'stats.result.draw': 'Ничья',
  },
  en: {
    'nav.home': 'Home',
    'nav.matchmaking': 'Matchmaking',
    'nav.profile': 'Profile',
    'nav.stats': 'Stats',
    'nav.inventory': 'Inventory',
    'nav.clans': 'Clans',
    'nav.subscription': 'Premium',
    'nav.support': 'Support',
    'nav.admin': 'Admin Panel',

    'social.telegram': 'Telegram — CS Legasy',
    'social.youtube': 'YouTube — CS Legasy',
    'social.followUs': 'Follow us',

    'header.notifications': 'Notifications',
    'header.markAllRead': 'Mark all read',
    'header.noNotifications': 'No notifications',
    'header.userDefault': 'User',
    'header.level': 'Level {value}',
    'header.profile': 'Profile',
    'header.subscription': 'Premium',
    'header.logout': 'Log out',
    'header.logout_success': 'You have logged out successfully',

    'test.friend_request.title': 'New friend request',
    'test.friend_request.message': 'Player123 wants to add you as a friend',
    'test.match_found.title': 'Match found!',
    'test.match_found.message': 'Your match is ready. Go to the lobby.',
    'test.clan_invite.title': 'Clan invitation',
    'test.clan_invite.message': 'Clan "Elite Warriors" invites you to join',

    'time.just_now': 'Just now',
    'time.minutes_ago': '{value}m ago',
    'time.hours_ago': '{value}h ago',
    'time.locale': 'en-US',

    // Common
    'common.play': 'Play',
    'common.cancel': 'Cancel',
    'common.save': 'Save',
    'common.update': 'Refresh',
    'common.export': 'Export data',
    'common.search': 'Search',
    'common.filters': 'Filters',
    'common.filters_search': 'Filters & Search',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.page_of': 'Page {current} of {total}',
    'common.shown_of': 'Shown {shown} of {total}',
    'common.soon': 'Soon',

    // Lobby
    'lobby.title': 'Welcome to ROMAN FACEIT',
    'lobby.subtitle': 'Ready for new challenges? Find a match or explore your progress',
    'lobby.stat.matches': 'Matches played',
    'lobby.stat.winrate': 'Win rate',
    'lobby.stat.rating': 'Rating points',
    'lobby.stat.level': 'Level',
    'lobby.feature.find_match.title': 'Find match',
    'lobby.feature.find_match.desc': 'Join matchmaking and compete with other players',
    'lobby.feature.inventory.title': 'Inventory',
    'lobby.feature.inventory.desc': 'Manage your items and skins',
    'lobby.feature.clans.title': 'Clans',
    'lobby.feature.clans.desc': 'Join a clan or create your own',
    'lobby.feature.stats.title': 'Stats',
    'lobby.feature.stats.desc': 'View your stats and track progress',
    'lobby.friends_online': 'Friends online',
    'lobby.status.online': 'Online',
    'lobby.status.ingame': 'In game',
    'lobby.invite': 'Invite',
    'lobby.recent_matches': 'Recent matches',

    // Matchmaking
    'mm.title': 'Matchmaking',
    'mm.subtitle': 'Set your preferences and find a suitable match',
    'mm.type': 'Game type',
    'mm.type.solo': 'Solo',
    'mm.type.team': 'Team',
    'mm.map': 'Map',
    'mm.map.any': 'Any map',
    'mm.server': 'Server',
    'mm.server.any': 'Any server',
    'mm.mode': 'Game mode',
    'mm.start_search': 'Start search',
    'mm.stop_search': 'Stop search',
    'mm.toast.started': 'Matchmaking started! ',
    'mm.toast.stopped': 'Matchmaking stopped',
    'mm.status.searching': 'Searching for a match...',
    'mm.status.description': 'Looking for suitable players for your match',
    'mm.status.cancel': 'Cancel search',
    'mm.queue.info': 'Queue info',
    'mm.queue.players': 'Players in queue',
    'mm.queue.eta': 'Estimated time',
    'mm.queue.rank': 'Your rank',
    'mm.found.title': 'Match found!',
    'mm.found.map': 'Map:',
    'mm.found.mode': 'Mode:',
    'mm.found.server': 'Server:',
    'mm.found.avg_rank': 'Average rank:',
    'mm.accept': 'Accept',
    'mm.decline': 'Decline',
    'mm.toast.accepted': 'Match accepted! Connecting to server...',
    'mm.toast.declined': 'Match declined',

    // Profile
    'profile.edit_profile': 'Edit profile',
    'profile.tabs.overview': 'Overview',
    'profile.tabs.edit': 'Edit',
    'profile.tabs.achievements': 'Achievements',
    'profile.updated': 'Profile updated!',
    'profile.logout_success': 'You have logged out successfully',
    'profile.section.main': 'Main information',
    'profile.username': 'Username',
    'profile.username_placeholder': 'Enter username',
    'profile.email': 'Email',
    'profile.email_placeholder': 'Enter email',
    'profile.avatar': 'Avatar',
    'profile.background': 'Profile background',
    'profile.background_drop': 'Drag an image for the background or click to select',
    'profile.drop.active': 'Drop the file here',
    'profile.drop.idle': 'Drag an image or click to select',
    'profile.btn.cancel': 'Cancel',
    'profile.btn.logout': 'Log out',
    'profile.btn.save': 'Save',
    'profile.avatar_updated': 'Avatar updated!',
    'profile.level_elo': 'Level {level} • ELO {elo}',
    'profile.stats.matches': 'Matches',
    'profile.stats.wins': 'Wins',
    'profile.stats.losses': 'Losses',
    'profile.stats.winrate': 'Win rate',
    'profile.metrics.winrate': 'Win rate',
    'profile.metrics.winrate_desc': 'Percentage of matches won',
    'profile.metrics.kd': 'K/D Ratio',
    'profile.metrics.kd_desc': 'Kills to deaths ratio',
    'profile.metrics.total_matches': 'Total matches',
    'profile.metrics.total_matches_desc': 'Total number of matches played',
    'profile.metrics.best_map': 'Best map',
    'profile.metrics.best_map_desc': 'Map with the most matches played',
    'profile.metrics.avg_score': 'Average score',
    'profile.metrics.avg_score_desc': 'Average score per match',
    'profile.metrics.rank': 'Current rank',
    'profile.metrics.rank_desc': 'Your current system rank',
    'profile.achievements.soon': 'Achievements system will be added in upcoming updates',

    // Inventory
    'inventory.title': 'Inventory',
    'inventory.add_item': 'Add item',
    'inventory.stats': 'Stats',
    'inventory.total_items': 'Total items',
    'inventory.stattrak_items': 'StatTrak items',
    'inventory.legendary_items': 'Legendary items',
    'inventory.custom_items': 'Custom items',
    'inventory.filters_search': 'Filters & Search',
    'inventory.search': 'Search',
    'inventory.search_placeholder': 'Search by name...',
    'inventory.type': 'Item type',
    'inventory.type.all': 'All types',
    'inventory.type.weapon': 'Weapons',
    'inventory.type.knife': 'Knives',
    'inventory.type.glove': 'Gloves',
    'inventory.type.sticker': 'Stickers',
    'inventory.type.agent': 'Agents',
    'inventory.rarity': 'Rarity',
    'inventory.rarity.all': 'All rarities',
    'inventory.rarity.common': 'Common',
    'inventory.rarity.uncommon': 'Uncommon',
    'inventory.rarity.rare': 'Rare',
    'inventory.rarity.epic': 'Epic',
    'inventory.rarity.legendary': 'Legendary',
    'inventory.condition': 'Condition',
    'inventory.condition.all': 'All conditions',
    'inventory.condition.factory_new': 'Factory new',
    'inventory.condition.minimal_wear': 'Minimal wear',
    'inventory.condition.field_tested': 'Field-tested',
    'inventory.condition.well_worn': 'Well-worn',
    'inventory.condition.battle_scarred': 'Battle-scarred',
    'inventory.empty.title': 'Inventory is empty',
    'inventory.empty.desc': 'Your items will appear here',
    'inventory.inspect': 'Inspect',
    'inventory.toast.inspect': 'Inspect item: {name}',
    'inventory.toast.added': 'Item "{name}" added to inventory!',
    'inventory.delete': 'Delete',
    'inventory.sort': 'Sort',
    'inventory.sort.newest': 'Newest first',
    'inventory.sort.oldest': 'Oldest first',
    'inventory.sort.name_asc': 'Name A–Z',
    'inventory.sort.name_desc': 'Name Z–A',
    'inventory.sort.rarity_high': 'Rarity: high',
    'inventory.sort.rarity_low': 'Rarity: low',
    'inventory.sort.float_low': 'Float: low',
    'inventory.sort.float_high': 'Float: high',
    'inventory.loading': 'Loading inventory…',
    'inventory.page': 'Page {page} of {total}',
    'inventory.items_count': '{count} items',
    'inventory.add_first': 'Add your first item',

    // Stats
    'stats.title': 'Stats',
    'stats.export': 'Export data',
    'stats.refresh': 'Refresh',
    'stats.total_matches': 'Total matches',
    'stats.total_matches_delta': '+12% this month',
    'stats.next_level_in': 'Next level in: {value} matches',
    'stats.winrate': 'Win rate',
    'stats.winrate_goal': 'Goal: 70%',
    'stats.kd_ratio': 'K/D Ratio',
    'stats.kd_delta': '-0.05 this month',
    'stats.kd_avg': 'Average per match: 1.2',
    'stats.elo': 'ELO rating',
    'stats.elo_delta': '+150 this month',
    'stats.elo_next': 'To next rank: 250 ELO',
    'stats.avg_time': 'Average playtime',
    'stats.avg_time_delta': '+0.3h this week',
    'stats.total_time': 'Total playtime: 120h',
    'stats.avg_damage': 'Average damage',
    'stats.avg_damage_best': 'Best result: 156.7',
    'stats.filters': 'Filters',
    'stats.filter.map': 'Map',
    'stats.filter.all_maps': 'All maps',
    'stats.filter.period': 'Period',
    'stats.filter.last7': 'Last 7 days',
    'stats.filter.last30': 'Last 30 days',
    'stats.filter.last90': 'Last 90 days',
    'stats.filter.all_time': 'All time',
    'stats.filter.result': 'Result',
    'stats.filter.all_matches': 'All matches',
    'stats.filter.only_wins': 'Wins only',
    'stats.filter.only_losses': 'Losses only',
    'stats.performance_metrics': 'Performance metrics',
    'stats.player_skills': 'Player skills',
    'stats.wins_losses': 'Wins/Losses ratio',
    'stats.elo_trend': 'ELO trend',
    'stats.by_maps': 'Stats by maps',
    'stats.activity_by_days': 'Activity by days',
    'stats.chart_loading': 'Loading…',
    'stats.chart_empty': 'No data for selected filters',
    'stats.table.title': 'Match history',
    'stats.table.search_placeholder': 'Search by map or mode...',
    'stats.table.date': 'Date',
    'stats.table.map': 'Map',
    'stats.table.mode': 'Mode',
    'stats.table.server': 'Server',
    'stats.table.result': 'Result',
    'stats.table.score': 'Score',
    'stats.table.duration': 'Duration',
    'stats.table.actions': 'Actions',
    'stats.result.win': 'Win',
    'stats.result.loss': 'Loss',
    'stats.result.draw': 'Draw',
  },
};

translations.ru = { ...translations.ru, ...extraRu };
translations.en = { ...translations.en, ...extraEn };

export function translationKeys(lang: Language): string[] {
  return Object.keys(translations[lang]);
}

function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return Object.keys(params).reduce((acc, key) => {
    return acc.replace(new RegExp(`\\{${key}\\}`, 'g'), String(params[key]));
  }, template);
}

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const getInitial = (): Language => {
    const saved = localStorage.getItem('app_language');
    if (saved === 'ru' || saved === 'en') return saved;
    return navigator.language.startsWith('ru') ? 'ru' : 'en';
  };

  const [language, setLanguageState] = useState<Language>(getInitial);

  useEffect(() => {
    localStorage.setItem('app_language', language);
  }, [language]);

  const setLanguage = (lang: Language) => setLanguageState(lang);
  const toggleLanguage = () => setLanguageState((prev) => (prev === 'ru' ? 'en' : 'ru'));

  const t = useMemo(() => {
    return (key: string, params?: Record<string, string | number>) => {
      const dict = translations[language];
      const str = dict[key] ?? key;
      if (dict[key] === undefined && process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(`[i18n] missing key: ${key}`);
      }
      return interpolate(str, params);
    };
  }, [language]);

  const value: LanguageContextValue = {
    language,
    t,
    toggleLanguage,
    setLanguage,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return ctx;
}


